import './styles.scoped.css'
import {useState, useEffect} from 'react'
import {Typography, Checkbox, FormControlLabel, TextField, Autocomplete, Chip, Button, Skeleton} from '@mui/material'
import {Controller} from 'react-hook-form'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {useParams} from 'react-router-dom'
import {parseError} from '../../../../utils/helpers'
import {caregiverApi} from '../../../../services'
import {SkeletonContainer, FieldSkeleton, ButtonSkeleton, TextSkeleton, ChipSkeleton} from '../../../../components'

// A checkbox with its label, sized and offset like FormControlLabel.
const CheckSkeleton = ({width}) => (
    <div style={{display: 'inline-flex', alignItems: 'center', height: 42, marginLeft: -11, marginRight: 16}}>
        <Skeleton variant="rounded" width={18} height={18} sx={{m: 1.5, borderRadius: 0.5}} />
        <TextSkeleton width={width} fontSize={16} />
    </div>
)

const LanguageAndPreferencesSkeleton = () => (
    <SkeletonContainer>
        <TextSkeleton width={95} fontSize={12} />
        <CheckSkeleton width={170} />
        <br />
        <CheckSkeleton width={160} />
        <br />
        <CheckSkeleton width={380} />
        <br />
        <br />
        <TextSkeleton width={115} fontSize={12} />
        <div className="check">
            <CheckSkeleton width={36} />
            <CheckSkeleton width={56} />
        </div>
        <br />
        <TextSkeleton width={70} fontSize={12} />
        <div className="check">
            <CheckSkeleton width={64} />
            <CheckSkeleton width={70} />
            <CheckSkeleton width={52} />
            <CheckSkeleton width={92} />
        </div>
        <br />
        <FieldSkeleton labelWidth={100} />
        <div className="languange">
            <ChipSkeleton width={84} height={32} />
            <ChipSkeleton width={92} height={32} />
        </div>
        <br />
        <TextSkeleton width={140} fontSize={12} />
        <CheckSkeleton width={340} />
        <br />
        <br />
        <TextSkeleton width={135} fontSize={12} />
        <CheckSkeleton width={320} />
        <br />
        <CheckSkeleton width={230} />
        <br />
        <CheckSkeleton width={300} />
        <br />
        <br />
        <TextSkeleton width={175} fontSize={12} />
        <CheckSkeleton width={340} />
        <br />
        <br />
        <br />
        <div className="actions">
            <ButtonSkeleton width={80} />
            <ButtonSkeleton width={130} />
        </div>
    </SkeletonContainer>
)

export default () => {
    const [selectedLanguages, setSelectedLanguages] = useState([])
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {caregiverId} = useParams()
    const [skeleton, setSkeleton] = useState(true)
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                comfortableWithDogs: yup.boolean().default(false),
                comfortableWithCats: yup.boolean().default(false),
                ableToAssistWithPetCare: yup.boolean().default(false),
                man: yup.boolean().default(false),
                woman: yup.boolean().default(false),
                fullTime: yup.boolean().default(false),
                partTime: yup.boolean().default(false),
                liveIn: yup.boolean().default(false),
                hourlyShifts: yup.boolean().default(false),
                languages: yup.array().of(yup.string()),
                comfortableWorkingInaHouseholdWithSmokers: yup.boolean().default(false),
                hasValidDriversLicenseClient: yup.boolean().default(false),
                skilledInWoundCareManagement: yup.boolean().default(false),
                experiencedInPhysicalTherapyAssistance: yup.boolean().default(false),
                experiencedUsingMobilityEquipment: yup.boolean().default(false),
            }),
        ),
    })

    const languages = [
        {key: 'en', value: 'English'},
        {key: 'es', value: 'Spanish'},
        {key: 'fr', value: 'French'},
        {key: 'zh', value: 'Chinese'},
    ]

    useEffect(() => {
        reset(true)
    }, [])

    useEffect(() => {
        if (form.getValues('languages')) {
            selectedLanguages.length = 0
            form.getValues('languages').forEach(key => {
                selectedLanguages.push(languages.find(language => language.key == key))
            })
        }
    }, [])

    useEffect(() => {
        form.setValue(
            'languages',
            selectedLanguages.map(lang => lang.key),
        )
    }, [selectedLanguages])

    // The first load shows the skeleton; the Reset button refetches under the global spinner.
    const reset = (initial = false) => {
        if (!initial) setLoading(true)
        caregiverApi
            .getCaregiverPreferences(branchId, caregiverId)
            .then(({data}) => {
                if (!initial) setLoading(false)
                setSkeleton(false)
                form.setValue('languages', data?.languages)
                selectedLanguages.length = 0
                data?.languages?.forEach(key => {
                    selectedLanguages.push(languages.find(language => language.key == key))
                })
                form.setValue('comfortableWithDogs', data?.comfortable_with_dogs)
                form.setValue('comfortableWithCats', data?.comfortable_with_cats)
                form.setValue('ableToAssistWithPetCare', data?.assists_with_pet_care)
                form.setValue('man', data?.prefers_male)
                form.setValue('woman', data?.prefers_female)
                form.setValue('fullTime', data?.available_full_time)
                form.setValue('partTime', data?.available_part_time)
                form.setValue('liveIn', data?.available_live_in)
                form.setValue('hourlyShifts', data?.available_hourly_shifts)
                form.setValue('comfortableWorkingInaHouseholdWithSmokers', data?.comfortable_with_smokers)
                form.setValue('hasValidDriversLicenseClient', data?.has_valid_drivers_license)
                form.setValue('skilledInWoundCareManagement', data?.skilled_wound_care)
                form.setValue('experiencedInPhysicalTherapyAssistance', data?.experienced_physical_therapy)
                form.setValue('experiencedUsingMobilityEquipment', data?.experienced_hoyer_lift)
            })
            .catch(error => {
                if (!initial) setLoading(false)
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const submit = async data => {
        setLoading(true)
        caregiverApi
            .updateCaregiverPreference(branchId, caregiverId, {
                languages: data?.languages,
                comfortable_with_dogs: data?.comfortableWithDogs,
                comfortable_with_cats: data?.comfortableWithCats,
                assists_with_pet_care: data?.ableToAssistWithPetCare,
                prefers_male: data?.man,
                prefers_female: data?.woman,
                no_gender_preference: !data?.man && !data?.woman,
                available_full_time: data?.fullTime,
                available_part_time: data?.partTime,
                available_live_in: data?.liveIn,
                available_hourly_shifts: data?.hourlyShifts,
                comfortable_with_smokers: data?.comfortableWorkingInaHouseholdWithSmokers,
                has_valid_drivers_license: data?.hasValidDriversLicenseClient,
                skilled_wound_care: data?.skilledInWoundCareManagement,
                experienced_physical_therapy: data?.experiencedInPhysicalTherapyAssistance,
                experienced_hoyer_lift: data?.experiencedUsingMobilityEquipment,
            })
            .then(() => {
                setLoading(false)
                setMessageBox({
                    status: 'success',
                    title: 'Caregiver Informations Updated',
                    description: 'Caregiver Language and Preferences have been successfully updated. All changes are now reflected in the system.',
                })
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <>
            {skeleton && <LanguageAndPreferencesSkeleton />}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <Typography variant="inputLabel" color="text.secondary">
                    Pet Preferences
                </Typography>
                <br />
                <Controller
                    {...form.register('comfortableWithDogs')}
                    control={form.control}
                    render={({field}) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                            label="Comfortable with dogs"
                        />
                    )}
                />
                <br />
                <Controller
                    {...form.register('comfortableWithCats')}
                    control={form.control}
                    render={({field}) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                            label="Comfortable with cats"
                        />
                    )}
                />
                <br />
                <Controller
                    {...form.register('ableToAssistWithPetCare')}
                    control={form.control}
                    render={({field}) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                            label="Able to assist with pet care (feeding, walking, cleaning)"
                        />
                    )}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Gender Preferences
                </Typography>
                <br />
                <div className="check">
                    <Controller
                        {...form.register('man')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Man"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('woman')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Woman"
                            />
                        )}
                    />
                </div>
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Availability
                </Typography>
                <br />
                <div className="check">
                    <Controller
                        {...form.register('fullTime')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Full-time"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('partTime')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Part-time"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('liveIn')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Live-in"
                            />
                        )}
                    />
                    <Controller
                        {...form.register('hourlyShifts')}
                        control={form.control}
                        render={({field}) => (
                            <FormControlLabel
                                control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                                label="Hourly shifts"
                            />
                        )}
                    />
                </div>
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Language Spoken
                </Typography>
                <Autocomplete
                    multiple
                    options={languages}
                    value={selectedLanguages}
                    onChange={(e, newVal) => setSelectedLanguages(newVal)}
                    isOptionEqualToValue={(opt, val) => opt.key === val.key}
                    getOptionLabel={opt => opt.value}
                    renderInput={params => <TextField {...params} placeholder="Add your languages" />}
                    slotProps={{chip: {sx: {display: 'none'}}}}
                />
                <div className="languange">
                    {selectedLanguages.map(item => (
                        <Chip
                            key={item.key}
                            label={item.value}
                            onDelete={() => setSelectedLanguages(prev => prev.filter(p => p.key !== item.key))}
                            variant="contained"
                            size="medium"
                        />
                    ))}
                </div>
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Environment & Lifestyle
                </Typography>
                <br />
                <Controller
                    {...form.register('comfortableWorkingInaHouseholdWithSmokers')}
                    control={form.control}
                    render={({field}) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                            label="Comfortable working in a household with smokers"
                        />
                    )}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Additional Care Skills
                </Typography>
                <br />
                <Controller
                    {...form.register('hasValidDriversLicenseClient')}
                    control={form.control}
                    render={({field}) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                            label="Has valid driver’s license / can drive client"
                        />
                    )}
                />
                <br />
                <Controller
                    {...form.register('skilledInWoundCareManagement')}
                    control={form.control}
                    render={({field}) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                            label="Skilled in wound care management"
                        />
                    )}
                />
                <br />
                <Controller
                    {...form.register('experiencedInPhysicalTherapyAssistance')}
                    control={form.control}
                    render={({field}) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                            label="Experienced in physical therapy assistance"
                        />
                    )}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Medical equipment assistance
                </Typography>
                <br />
                <Controller
                    {...form.register('experiencedUsingMobilityEquipment')}
                    control={form.control}
                    render={({field}) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value ?? false} onChange={e => field.onChange(e.target.checked)} />}
                            label="Experienced using Hoyer lift / mobility equipment"
                        />
                    )}
                />
                <br />
                <br />
                <br />
                <div className="actions">
                    <Button variant="outlined" onClick={() => reset()}>
                        Reset
                    </Button>
                    <Button variant="contained" onClick={form.handleSubmit(submit)}>
                        Save Changes
                    </Button>
                </div>
            </div>
        </>
    )
}
